/**
 * Mapper functions for transforming API responses to frontend models
 */
import { mapEntryToApiRequest } from '../ProposalShow/types';
/**
 * Maps the API response to the frontend Proposal model
 * @param response - The API response data
 * @returns The mapped Proposal object
 */
export function mapProposalResponseToProposal(response) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const { proposal } = response;
    return {
        id: proposal.id,
        // General Information fields
        priceList: proposal.priceList,
        projectName: proposal.projectName || '',
        proposalNumber: proposal.proposalNumber || '',
        proposalDate: proposal.proposalDate || '',
        proposalType: proposal.proposalType,
        emailUrlUuid: proposal.emailUrlUuid,
        enablePriceList: proposal.enablePriceList,
        enableTaxGroups: proposal.enableTaxGroups,
        building: proposal.building,
        po: proposal.po || '',
        // Financial fields
        cost: proposal.cost != null ? Number(proposal.cost) : undefined,
        grossMargin: proposal.grossMargin != null ? Number(proposal.grossMargin) : undefined,
        subTotal: proposal.subTotal != null ? Number(proposal.subTotal) : undefined,
        salesTax: proposal.salesTax != null ? Number(proposal.salesTax) : undefined,
        tax: proposal.tax,
        totalAmount: proposal.totalAmount != null ? Number(proposal.totalAmount) : undefined,
        showProfitMargin: proposal.showProfitMargin != null ? Boolean(proposal.showProfitMargin) : false,
        // Status and metadata
        status: proposal.status,
        statusId: proposal.statusId,
        notes: proposal.notes,
        createdAt: proposal.createdAt,
        updatedAt: proposal.updatedAt,
        // Rich Content sections
        summaryEntries: proposal.summaryEntries || [],
        scopeOfWorkEntries: proposal.scopeOfWorkEntries || [],
        exclusionsEntries: proposal.exclusionsEntries || [],
        termsAndConditionsEntries: proposal.termsAndConditionsEntries || [],
        // Additional data
        customer: proposal.customer,
        preparedFor: proposal.preparedFor,
        deficiencies: proposal.deficiencies || [],
        materialsAndServices: proposal.materialsAndServices || [],
        // Related items
        relatedItems: proposal.relatedItems || [],
        // Tags
        tagList: proposal.tagList || [],
        // Create button logic data
        restrictedFinancialControls: proposal.restrictedFinancialControls != null ? Boolean(proposal.restrictedFinancialControls) : false,
        proposalRequest: proposal.proposalRequest != null ? Boolean(proposal.proposalRequest) : false,
        hasInvoices: proposal.hasInvoices != null ? Boolean(proposal.hasInvoices) : false,
        hasChildInspection: proposal.hasChildInspection != null ? Boolean(proposal.hasChildInspection) : false,
        hasInspectionSeries: proposal.hasInspectionSeries != null ? Boolean(proposal.hasInspectionSeries) : false,
        tenantCapabilities: {
            hasInvoicing: ((_a = proposal.tenantCapabilities) === null || _a === void 0 ? void 0 : _a.hasInvoicing) != null ? Boolean(proposal.tenantCapabilities.hasInvoicing) : false,
            hasService: ((_b = proposal.tenantCapabilities) === null || _b === void 0 ? void 0 : _b.hasService) != null ? Boolean(proposal.tenantCapabilities.hasService) : false,
            hasInspectionSeries: ((_c = proposal.tenantCapabilities) === null || _c === void 0 ? void 0 : _c.hasInspectionSeries) != null
                ? Boolean(proposal.tenantCapabilities.hasInspectionSeries)
                : false,
            hasPayrixMerchant: ((_d = proposal.tenantCapabilities) === null || _d === void 0 ? void 0 : _d.hasPayrixMerchant) != null
                ? Boolean(proposal.tenantCapabilities.hasPayrixMerchant)
                : false,
            enableBackendPayments: ((_e = proposal.tenantCapabilities) === null || _e === void 0 ? void 0 : _e.enableBackendPayments) != null
                ? Boolean(proposal.tenantCapabilities.enableBackendPayments)
                : false,
            allowMultipleInvoices: ((_f = proposal.tenantCapabilities) === null || _f === void 0 ? void 0 : _f.allowMultipleInvoices) != null
                ? Boolean(proposal.tenantCapabilities.allowMultipleInvoices)
                : false
        },
        userPermissions: {
            isSales: ((_g = proposal.userPermissions) === null || _g === void 0 ? void 0 : _g.isSales) != null ? Boolean(proposal.userPermissions.isSales) : false,
            canCreateInspection: ((_h = proposal.userPermissions) === null || _h === void 0 ? void 0 : _h.canCreateInspection) != null
                ? Boolean(proposal.userPermissions.canCreateInspection)
                : false,
            managePaymentMethods: ((_j = proposal.userPermissions) === null || _j === void 0 ? void 0 : _j.managePaymentMethods) != null
                ? Boolean(proposal.userPermissions.managePaymentMethods)
                : false,
            canConvertProposalRequest: ((_k = proposal.userPermissions) === null || _k === void 0 ? void 0 : _k.canConvertProposalRequest) != null
                ? Boolean(proposal.userPermissions.canConvertProposalRequest)
                : false
        },
        // Collect button logic data
        showChargeButton: proposal.showChargeButton != null ? Boolean(proposal.showChargeButton) : false
    };
}
/**
 * Maps frontend model to API request format
 * @param proposalUpdate - The frontend update object
 * @returns The API-formatted update object
 */
export function mapProposalUpdateToApiRequest(proposalUpdate) {
    const mappedUpdate = {};
    // Map camelCase to snake_case for API
    Object.entries(proposalUpdate).forEach(([key, value]) => {
        switch (key) {
            // Rich content entries mapping
            case 'summaryEntries':
                mappedUpdate.proposal_summaries_attributes = value.map((entry) => mapEntryToApiRequest(entry));
                break;
            case 'scopeOfWorkEntries':
                mappedUpdate.proposal_scope_of_works_attributes = value.map((entry) => mapEntryToApiRequest(entry));
                break;
            case 'exclusionsEntries':
                mappedUpdate.proposal_exclusions_attributes = value.map((entry) => mapEntryToApiRequest(entry));
                break;
            case 'termsAndConditionsEntries':
                mappedUpdate.proposal_terms_and_conditions_attributes = value.map((entry) => mapEntryToApiRequest(entry));
                break;
            case 'buildingId':
                mappedUpdate.building_id = value;
                break;
            case 'proposalNumber':
                mappedUpdate.proposal_number = value;
                break;
            case 'proposalDate':
                mappedUpdate.proposal_date = value;
                break;
            case 'proposalTypeId':
                mappedUpdate.proposal_type_id = value;
                break;
            case 'priceListId':
                mappedUpdate.price_list_id = value;
                break;
            case 'taxRateId':
                mappedUpdate.tax_item_id = value;
                break;
            case 'projectName':
                mappedUpdate.project_name = value;
                break;
            case 'statusId':
                mappedUpdate.proposal_status_id = value;
                break;
            case 'poNumber':
                mappedUpdate.po_number = value;
                break;
            case 'totalAmount':
                mappedUpdate.total_amount = value;
                break;
            case 'subTotal':
                mappedUpdate.sub_total = value;
                break;
            case 'salesTax':
                mappedUpdate.sales_tax = value;
                break;
            case 'tax':
                mappedUpdate.tax = value;
                break;
            case 'showProfitMargin':
                mappedUpdate.use_profit_margin = value;
                break;
            case 'customerName':
                mappedUpdate.customer_name = value;
                break;
            case 'customerEmail':
                mappedUpdate.customer_email = value;
                break;
            case 'customerPhone':
                mappedUpdate.customer_phone = value;
                break;
            case 'customerAddress':
                mappedUpdate.customer_address = value;
                break;
            case 'customerCity':
                mappedUpdate.customer_city = value;
                break;
            case 'customerState':
                mappedUpdate.customer_state = value;
                break;
            case 'customerZip':
                mappedUpdate.customer_zip = value;
                break;
            case 'customerCountry':
                mappedUpdate.customer_country = value;
                break;
            case 'preparedFor':
                mappedUpdate.prepared_for = value;
                break;
            case 'preparedForEmail':
                mappedUpdate.prepared_for_email = value;
                break;
            case 'preparedForPhone':
                mappedUpdate.prepared_for_phone = value;
                break;
            case 'preparedForJobTitle':
                mappedUpdate.prepared_for_title = value;
                break;
            case 'tagList':
                mappedUpdate.tag_list = value;
                break;
            default:
                // For fields that have the same name in both formats
                mappedUpdate[key] = value;
        }
    });
    return mappedUpdate;
}
